import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import noVisitada from '../icons/noVisitada.svg';
import notaNV from '../icons/notaNV.svg';
import { withRouter } from '../providers/withRouter';
import Swal from 'sweetalert2';
import { Tooltip } from '@mui/material';
import { UsuarioContext } from '../providers/userContext';
import { noRealizadaAAFF } from '../services';
import { formatoFechas, formatoFechasEs } from '../functions/funciones';
import { toast } from 'react-toastify';

// group_id de r_tech_aaffs: 1 = CAE, 4 = KAE
const SERVICIOS = {
  1: { nombre: 'CAE', flag: 'unRealized', fecha: 'unRealizedDate', motivo: 'observacionNoVisita', comentario: 'comentarioNoVisita' },
  4: { nombre: 'KAE', flag: 'unRealizedKae', fecha: 'unRealizedDateKae', motivo: 'unRealizedObservationsKae', comentario: 'unRealizedCommentsKae' },
};
// La X del AAFF siempre es magenta, sea cual sea el servicio
const FILTRO_X_AAFF = 'invert(13%) sepia(100%) saturate(3000%) hue-rotate(300deg) brightness(80%) contrast(120%)';
const MAX_COMENTARIO = 200;

// servicio: 'CAE' (ventas/comunidades), 'KAE' (instalaciones) o sin indicar (listado de AAFF)
// fallback: lo que muestra la fila cuando el AAFF no tiene marca (su propio no visitado),
// para que al quitar la marca del AAFF aparezca sin recargar
const NoVisitadoAAFF = ({ aaff: aaffInicial, servicio, fallback = null }) => {
  const { t } = useTranslation('common');
  const user = useContext(UsuarioContext);
  const [aaff, setAaff] = useState(aaffInicial || {});

  const esTecnico = user.rol === 'TECH' || user.rol === 'TECH_COOR';
  const fijos = servicio === 'CAE' ? [1] : servicio === 'KAE' ? [4] : null;
  // Servicios que lleva el usuario en este AAFF (el backend lo vuelve a comprobar al guardar)
  const misServicios = fijos || [...new Set((aaff.technicians || [])
    .filter((tec) => tec.id === user.id)
    // r_tech_aaffs llega como lista: una fila por servicio asignado
    .flatMap((tec) => [].concat(tec.r_tech_aaffs || []).map((r) => r.group_id)))]
    .filter((g) => SERVICIOS[g])
    .sort();
  const visibles = esTecnico ? misServicios : (fijos || [1, 4]);
  const marcados = visibles.filter((g) => aaff[SERVICIOS[g].flag]);
  const puedeEditar = esTecnico && misServicios.length > 0;

  const abrir = async () => {
    const unSoloServicio = misServicios.length === 1;
    // KAE solo tiene sentido con el segundo motivo (todavía no empezamos las visitas)
    const OPCIONES_POR_SERVICIO = {
      1: [t('visits.aaffNoWantRenewal'), t('visits.aaffNotStartYet')],
      4: [t('visits.aaffNotStartYet')],
    };
    const fila = (g) => {
      const s = SERVICIOS[g];
      const opciones = OPCIONES_POR_SERVICIO[g] || [];
      return `
        <div style="text-align:left; margin-top:12px;">
          ${unSoloServicio
            // Con un solo servicio no hay nada que elegir ni que indicar: guardar ya lo marca
            ? ''
            : `<label><input type="checkbox" id="check-${g}" ${aaff[s.flag] ? 'checked' : ''}/> <b>${s.nombre}</b></label>`}
          <select id="motivo-${g}" class="swal2-select" style="width:100%; margin:6px 0;">
            ${opciones.map((o) => `<option ${aaff[s.motivo] === o ? 'selected' : ''}>${o}</option>`).join('')}
          </select>
          <textarea id="comentario-${g}" maxlength="${MAX_COMENTARIO}" placeholder="${t('visits.specifyReason')}" class="swal2-textarea" style="width:100%; height:80px; margin:0;"></textarea>
        </div>`;
    };

    const result = await Swal.fire({
      title: t('visits.noVisitReason'),
      html: misServicios.map(fila).join(''),
      confirmButtonText: t('actions.save'),
      confirmButtonColor: '#05811b',
      showCancelButton: true,
      cancelButtonText: t('actions.cancel'),
      cancelButtonColor: '#d33',
      // Quita de golpe los motivos de todos los servicios del tecnico
      showDenyButton: misServicios.some((g) => aaff[SERVICIOS[g].flag]),
      denyButtonText: t('visits.removeNoVisitReason'),
      denyButtonColor: '#33383b',
      focusConfirm: false,
      didOpen: () => {
        const popup = Swal.getPopup();
        // Los comentarios se rellenan aqui para no tener que escapar el HTML
        misServicios.forEach((g) => {
          popup.querySelector(`#comentario-${g}`).value = aaff[SERVICIOS[g].comentario] || '';
        });
      },
      preConfirm: () => {
        const popup = Swal.getPopup();
        const body = {};
        misServicios.forEach((g) => {
          const s = SERVICIOS[g];
          const marcado = unSoloServicio || popup.querySelector(`#check-${g}`).checked;
          body[s.flag] = marcado ? true : null;
          // Si ya estaba marcado se conserva la fecha original
          body[s.fecha] = marcado ? (aaff[s.fecha] || formatoFechas(new Date())) : null;
          body[s.motivo] = marcado ? popup.querySelector(`#motivo-${g}`).value : null;
          body[s.comentario] = marcado ? (popup.querySelector(`#comentario-${g}`).value.trim() || null) : null;
        });
        return body;
      },
    });

    if (result.isDenied) {
      await quitar();
      return;
    }
    if (!result.isConfirmed) return;

    // Si se desmarca algun servicio que estaba marcado, se pide confirmacion
    const seQuitan = misServicios.filter((g) => aaff[SERVICIOS[g].flag] && !result.value[SERVICIOS[g].flag]);
    if (seQuitan.length && !(await confirmarEliminar())) return;

    await guardar(result.value);
  };

  const confirmarEliminar = async () => {
    const confirmacion = await Swal.fire({
      title: t('visits.deleteNoVisitReason'),
      confirmButtonText: t('status.yes'),
      confirmButtonColor: '#05811b',
      showCancelButton: true,
      cancelButtonText: t('status.no'),
      cancelButtonColor: '#d33',
      focusConfirm: false,
    });
    return confirmacion.isConfirmed;
  };

  // Desde ventas, comunidades e instalaciones: pulsar la X elimina el motivo de ese servicio
  const quitar = async () => {
    if (!(await confirmarEliminar())) return;
    const body = {};
    misServicios.forEach((g) => {
      const s = SERVICIOS[g];
      [s.flag, s.fecha, s.motivo, s.comentario].forEach((campo) => { body[campo] = null; });
    });
    await guardar(body);
  };

  const guardar = async (body) => {
    // noRealizadaAAFF no lanza: devuelve los datos o undefined si falla
    const res = await noRealizadaAAFF(aaff.id, body);
    if (res) {
      setAaff((prev) => ({ ...prev, ...body }));
      toast.success(t('visits.dataSavedSuccess'));
    } else {
      toast.error(t('visits.dataUpdateError'));
    }
  };

  if (!marcados.length) {
    // En ventas, comunidades e instalaciones, sin marca del AAFF se muestra el no visitado de la fila
    if (fijos) return fallback;
    return puedeEditar ? (
      <button type="button" onClick={abrir}>
        <img src={notaNV} alt="No visitado" className="tableIcon" />
      </button>
    ) : null;
  }

  const tooltip = (
    <>
      {marcados.map((g) => {
        const s = SERVICIOS[g];
        return (
          <div key={g}>
            <b>{s.nombre}:</b> {formatoFechasEs(aaff[s.fecha])} - {aaff[s.motivo]}
            {aaff[s.comentario] && <div style={{ whiteSpace: 'pre-line' }}>{aaff[s.comentario]}</div>}
          </div>
        );
      })}
    </>
  );

  // Al pulsar: en ventas, comunidades e instalaciones se elimina el motivo; en el listado de AAFF se abre el modal
  const alPulsar = fijos ? quitar : abrir;

  return (
    <Tooltip title={tooltip}>
      <button type="button" onClick={puedeEditar ? alPulsar : undefined}>
        <img src={noVisitada} alt="No visitado" className="tableIcon" style={{ filter: FILTRO_X_AAFF }} />
      </button>
    </Tooltip>
  );
};

export default withRouter(NoVisitadoAAFF);
